<template>
  <div>
    <MapRawSource id="courses" :source="source">
      <MapRawLayer v-for="layer in layers" :layer="layer" :key="layer.id" />
    </MapRawSource>

    <MapIcon
      v-for="difficulty of difficulties"
      :as="difficulty || 'not-specified'"
    >
      <DifficultyIcon :difficulty="difficulty" />
    </MapIcon>
  </div>
</template>
<script lang="ts" setup>
import Mapbox from "mapbox-gl";
import type { Style, MapMouseEvent } from "mapbox-gl";
import type { Feature, Polygon } from "geojson";

const difficulties = ["beginner", "intermediate", "expert", null] as const;

export type CoursesLayerItem = {
  id: string;
  name: string;
  description: string;
  difficulty: "expert" | "beginner" | "intermediate" | null;
  startPoint: { coordinates: [number, number] };
};

const { Point } = Mapbox;

const runtimeConfig = useRuntimeConfig();

const emit = defineEmits({
  click: (e: MapMouseEvent, features: CoursesLayerItem[]) => true,
});

const router = useRouter();

const supabaseUrl = useSupabaseUrl();

const source = {
  tiles: [`${supabaseUrl}/functions/v1/tiles/{z}/{x}/{y}/courses.mvt`],
  type: "vector",
} as Style["sources"][0];

const map = useMap();

const layers = [
  {
    id: "course-points",
    type: "symbol",
    source: "courses",
    "source-layer": "courses",
    layout: {
      "icon-image": [
        "match",
        ["get", "difficulty"],
        "beginner",
        "beginner",
        "intermediate",
        "intermediate",
        "expert",
        "expert",
        "not-specified",
      ],
      "icon-size": 0.75,
      "icon-allow-overlap": true,
    },
  },
  {
    id: "course-labels",
    type: "symbol",
    source: "courses",
    "source-layer": "courses",
    layout: {
      "text-field": ["coalesce", ["get", "name_en"], ["get", "name"]],
      "text-anchor": "left",
      "text-size": 10,
      "text-offset": [1, 0],
    },
    paint: {
      "text-opacity": 0.7,
      "text-color": "#000000",
      "text-halo-color": "#FFFFFF",
      "text-halo-width": 1.25,
    },
  },
] as Style["layers"];

useMapOn("mouseenter", "course-points", (e) => {
  exists(map.value);
  map.value.getCanvas().style.cursor = "pointer";
});

useMapOn("mouseleave", "course-points", (e) => {
  exists(map.value);
  map.value.getCanvas().style.cursor = "";
});

useMapOn("click", (e) => {
  exists(e, "click events must exist");

  const p = e.point;
  const margin = 20;

  const coords = [
    e.target.unproject(new Point(p.x - margin, p.y - margin)).toArray(),
    e.target.unproject(new Point(p.x + margin, p.y - margin)).toArray(),
    e.target.unproject(new Point(p.x + margin, p.y + margin)).toArray(),
    e.target.unproject(new Point(p.x - margin, p.y + margin)).toArray(),
    e.target.unproject(new Point(p.x - margin, p.y - margin)).toArray(),
  ];

  const features = e.target.querySourceFeatures("courses", {
    sourceLayer: "courses",
    filter: [
      "within",
      {
        type: "Feature",
        geometry: {
          type: "Polygon",
          coordinates: [coords],
        },
      } as Feature<Polygon>,
    ],
  });

  try {
    if (features.length > 0) {
      emit(
        "click",
        e,
        features
          .map((f) => ({
            ...f.properties,
            startPoint: f.geometry,
          }))
          .filter((o, i, a) => i == a.findIndex((e) => e.id == o.id))
      );
    }
  } catch (x) {
    console.log("Fark: ", x);
    debugger;
  }
});
</script>
<style scoped>
.checkpoints {
  pointer-events: none;
}
</style>
